import inlineSVG from '@gearset/web-core/scripts/inlineSvg.js';

import analytics from 'Scripts/analytics.js';
import trackingCookieHelper from 'Scripts/trackingInformationCookieHelper.js';

class SummitHero {
	constructor(container) {
		/* Canvas */
		this.canvas = container;
		this.context = container.getContext('2d');
		this.createCanvases();

		/* Image data */
		this.aspectRatio = 1920 / 1080;
		this.sourceImage = null;
		this.displacementMapImage = null;

		this.sourceImageData = null;
		this.displacementMapData = null;

		/* Displacement data */
		this.displacement = this.getDisplacementIntensity();
		this.fluteWidth = this.getFluteWidth();
		this.tilingAmount = Math.floor(window.innerWidth / this.fluteWidth);
		this.horizontalIntensity = 2;
		this.verticalIntensity = 1;

		/* Mouse position */
		this.mouseNormalizedX = 1;
		this.mouseNormalizedY = 1;
		this.listenToMouse();

		/* Viewport size */
		this.resizeTimeout = null;
		this.listenToResize();

		/* Load images */
		this.loadImages([
			'/images/minisites/summit-2025-1/scene.jpg',
			'/images/minisites/summit-2025-1/dis-line.jpg',
		], images => {
			this.sourceImage = images[0];
			this.displacementMapImage = images[1];
			this.init();
			this.loop();
		});
	}

	/* Load given array of images */
	loadImages(imageSources, callback) {
		const images = [];
		let imagesLoaded = 0;

		for (const [index, src] of imageSources.entries()) {
			const img = new Image();
			img.src = src;

			img.addEventListener('load', () => {
				images[index] = img;
				imagesLoaded++;

				if (imagesLoaded === imageSources.length) {
					callback(images);
				}
			});

			img.addEventListener('error', () => {
				console.error(`Failed to load image: ${src}`);
			});
		}
	}

	/* Create hidden canvases for source and displacement map images */
	createCanvases() {
		if (!this.canvasSource) {
			this.canvasSource = document.createElement('canvas');
			this.contextSource = this.canvasSource.getContext('2d');
		}

		if (!this.canvasDisplacementMap) {
			this.canvasDisplacementMap = document.createElement('canvas');
			this.contextDisplacementMap = this.canvasDisplacementMap.getContext('2d');
		}
	}

	/* Set dimensions for the given canvas element */
	setCanvasDimensions(canvas) {
		const windowWidth = window.innerWidth;
		const windowHeight = window.innerHeight;

		if (windowWidth / windowHeight > this.aspectRatio) {
			canvas.width = Math.round(windowHeight * this.aspectRatio);
			canvas.height = windowHeight;
		} else {
			canvas.width = windowWidth;
			canvas.height = Math.round(windowWidth / this.aspectRatio);
		}
	}

	/* Determine fluteWidth based on window width */
	getFluteWidth() {
		if (window.innerWidth >= 1024 && window.innerWidth <= 1217) {
			return 60;
		}

		if (window.innerWidth >= 768 && window.innerWidth <= 1023) {
			return 50;
		}

		if (window.innerWidth > 600 && window.innerWidth <= 768) {
			return 40;
		}

		if (window.innerWidth < 600) {
			return 26;
		}

		return 75;
	}

	/* Determine displacement intensity based on window width */
	getDisplacementIntensity() {
		if (window.innerWidth > 768 && window.innerWidth <= 1099) {
			return 10;
		}

		if (window.innerWidth > 425 && window.innerWidth <= 768) {
			return 4;
		}

		if (window.innerWidth <= 425) {
			return 0;
		}

		return 30;
	}

	/* Update mouse coordinate values */
	listenToMouse() {
		window.addEventListener('mousemove', event => {
			this.mouseNormalizedX = 1.5 - ((event.clientX / window.innerWidth) * this.horizontalIntensity);
			this.mouseNormalizedY = 1.5 - ((event.clientY / window.innerHeight) * this.verticalIntensity);
		});
	}

	listenToResize() {
		window.addEventListener('resize', () => {
			clearTimeout(this.resizeTimeout);

			this.resizeTimeout = setTimeout(() => {
				this.fluteWidth = this.getFluteWidth();
				this.displacement = this.getDisplacementIntensity();
				this.tilingAmount = Math.floor(window.innerWidth / this.fluteWidth);

				if (this.sourceImage && this.displacementMapImage) {
					this.init();
				} else {
					console.error('Images are not yet loaded.');
				}
			}, 150);
		});
	}

	drawRepeatedDisplacementMap() {
		const tileWidth = this.canvasDisplacementMap.width / this.tilingAmount;
		const tileHeight = this.canvasDisplacementMap.height / this.tilingAmount;

		for (let row = 0; row < this.tilingAmount; row++) {
			for (let col = 0; col < this.tilingAmount; col++) {
				const x = col * tileWidth;
				const y = row * tileHeight;
				this.contextDisplacementMap.drawImage(this.displacementMapImage, 0, 0, this.displacementMapImage.width, this.displacementMapImage.height, x, y, tileWidth, tileHeight);
			}
		}

		this.displacementMapData = this.contextDisplacementMap.getImageData(0, 0, this.canvasDisplacementMap.width, this.canvasDisplacementMap.height).data;
	}

	init() {
		this.setCanvasDimensions(this.canvas);
		this.setCanvasDimensions(this.canvasSource);
		this.setCanvasDimensions(this.canvasDisplacementMap);

		const canvasWidth = this.canvas.width;
		const canvasHeight = this.canvas.height;

		const scale = Math.max(canvasWidth / this.sourceImage.width, canvasHeight / this.sourceImage.height);
		const drawWidth = this.sourceImage.width * scale;
		const drawHeight = this.sourceImage.height * scale;
		const offsetX = (canvasWidth - drawWidth) / 2;
		const offsetY = (canvasHeight - drawHeight) / 2;

		this.contextSource.clearRect(0, 0, canvasWidth, canvasHeight);
		this.contextSource.drawImage(this.sourceImage, offsetX, offsetY, drawWidth, drawHeight);

		this.sourceImageData = this.contextSource.getImageData(0, 0, canvasWidth, canvasHeight).data;

		this.drawRepeatedDisplacementMap();
	}

	loop() {
		window.requestAnimationFrame(() => {
			this.update();
			this.loop();
		});
	}

	update() {
		const canvasWidth = this.canvas.width;
		const canvasHeight = this.canvas.height;

		const displacementX = (this.mouseNormalizedX * this.displacement) * 0.7;
		const displacementY = (this.mouseNormalizedY * this.displacement) + 10;

		const outputImageData = this.context.createImageData(canvasWidth, canvasHeight);

		for (let y = 0; y < canvasHeight; y++) {
			for (let x = 0; x < canvasWidth; x++) {
				const pixelIndex = (y * canvasWidth) + x;
				const dataIndex = pixelIndex * 4;

				const displacementDepth = this.displacementMapData[dataIndex] / 255;

				let offsetX = Math.round(x + (displacementX * displacementDepth));
				let offsetY = Math.round(y + (displacementY * displacementDepth));

				offsetX = Math.max(0, Math.min(canvasWidth - 1, offsetX));
				offsetY = Math.max(0, Math.min(canvasHeight - 1, offsetY));

				const targetPixelIndex = (offsetY * canvasWidth) + offsetX;
				const targetDataIndex = targetPixelIndex * 4;

				outputImageData.data[dataIndex] = this.sourceImageData[targetDataIndex];
				outputImageData.data[dataIndex + 1] = this.sourceImageData[targetDataIndex + 1];
				outputImageData.data[dataIndex + 2] = this.sourceImageData[targetDataIndex + 2];
				outputImageData.data[dataIndex + 3] = this.sourceImageData[targetDataIndex + 3];
			}
		}

		this.context.putImageData(outputImageData, 0, 0);
	}
}


document.addEventListener('DOMContentLoaded', () => {
	/* Load SVGs inline, so we can colour them */
	inlineSVG.init({
		svgSelector: 'img.svg',
		initClass: 'js-inlinesvg',
	}, () => {
		document.documentElement.classList.add('is-loaded');
	});

	analytics.trackTaggedAnchors();
	trackingCookieHelper.storeTrackingInformationInCookie();

	// Listen for scrolls and trigger menu background
	window.addEventListener('scroll', () => {
		document.documentElement.classList.toggle('is-scrolled', window.scrollY > 0);
	});
	window.dispatchEvent(new Event('scroll'));

	/* Initialise hero */
	new SummitHero(document.querySelector('#scene'));
});
